import { Box, useMediaQuery } from '@mui/material';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Focused } from 'react-credit-cards-2';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import Input from '~/components/Input';
import CardComponent from '~/pages/Payment/CardCredit';

import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import axios from 'axios';
import { usePaymentGatewayPublicKey } from '~/hooks/usePaymentGatewayPublicKey';
import api from '~/services/api';
import { storeState } from '~/store';
import { getErrorMessage, logErrorWithContext } from '~/utils/errorHandler';
import { reportError } from '~/utils/reportError';
import { CreateTokenCardResponse } from '../../renewal/interfaces/card-token-response';
import { cardFormContainer, cardFormInputsContainer, headerContainer, inputValidtyFormContainer } from '../styles';

import schema from '../validation';
export type Inputs = {
  number: string;
  cvv: string;
  expiry: string;
  name: string;
  zipcode: string;
  state: string;
  city: string;
  neighborhood: string;
  street: string;
  numberAddress: string;
};

interface CardAjustProps {
  contractCode: string;
  /** Id do contrato (hash) usado na rota da API */
  contractId: string;
  setSucessRequest: Dispatch<SetStateAction<boolean>>;
  contractPaymentMethod: 'boleto' | 'credit_card';
  organizationId?: string | null;
}

export function CardAjust({
  contractCode,
  contractId,
  setSucessRequest,
  contractPaymentMethod,
  organizationId
}: CardAjustProps) {
  const showCard = useMediaQuery('(min-width:420px)');
  const {
    publicKey: paymentGatewayPublicKey,
    loading: loadingPublicKey,
    error: publicKeyError
  } = usePaymentGatewayPublicKey(organizationId);
  const PAGARME_URL = import.meta.env.VITE_PAGARME_API_URL;
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, dirtyFields }
  } = useForm<Inputs>({
    resolver: yupResolver(schema)
  });

  const [allIsEmpty, setAllIsEmpty] = useState(true);
  const [focused, setfocused] = useState<Focused>('number');
  const [loading, setLoading] = useState(false);

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    const expiryMonth = data.expiry.split('/')[0];
    const expiryYear = data.expiry.split('/')[1];

    const createTokenCardPayload = {
      type: 'card',
      card: {
        number: data.number.replaceAll(' ', '').trim(),
        exp_month: parseInt(expiryMonth),
        exp_year: parseInt(expiryYear),
        holder_name: data.name,
        cvv: data.cvv.trim()
      }
    };

    if (!paymentGatewayPublicKey) {
      storeState.addToast({
        title: 'Configuração de pagamento',
        message:
          publicKeyError?.message ??
          'Chave de pagamento da organização não disponível. Acesse com o link da organização (parâmetro org).',
        type: 'error'
      });
      return;
    }

    setLoading(true);

    try {
      const createTokenCardResponse = await axios.post<CreateTokenCardResponse>(
        `${PAGARME_URL}/tokens?appId=${paymentGatewayPublicKey}`,
        {
          ...createTokenCardPayload
        }
      );

      const condicionalUrl =
        contractPaymentMethod === 'credit_card'
          ? `/Contracts/${contractId}/card`
          : `/Contracts/${contractId}/subscription`;

      const payload: { card_token: string; organization_id: string } = {
        card_token: createTokenCardResponse.data.id,
        organization_id: organizationId ?? ''
      };

      await api
        .v2!.patch(condicionalUrl, payload)
        .then((response) => {
          if (response.status === 200) {
            storeState.addToast({
              title: 'Sucesso',
              message: 'Cartão atualizado com sucesso.',
              type: 'success'
            });
            setSucessRequest(true);
          }
        })
        .catch((error: any) => {
          setSucessRequest(false);

          // Log do erro com contexto
          logErrorWithContext(error, 'CardAjust_updateCard', {
            contractCode
          });

          // Usar o sistema unificado de tratamento de erros
          const errorResponse = getErrorMessage(error);
          storeState.addToast(errorResponse);

          // Log adicional para Rollbar
          reportError(`Erro ao atualizar cartão: ${errorResponse.title}`, error, {
            context: 'CardAjust_updateCard',
            contractCode
          });
        })
        .finally(() => {
          setLoading(false);
        });
    } catch (error: any) {
      setSucessRequest(false);
      setLoading(false);

      // Log do erro com contexto
      logErrorWithContext(error, 'CardAjust_createCardToken', {
        contractCode,
        cardDetails: {
          hasNumber: !!data.number,
          hasName: !!data.name,
          hasExpiry: !!data.expiry
        }
      });

      // Usar o sistema unificado de tratamento de erros
      const errorResponse = getErrorMessage(error, 'Verifique os dados do cartão e tente novamente.');
      storeState.addToast(errorResponse);

      // Log adicional para Rollbar
      reportError(`Erro ao criar token do cartão: ${errorResponse.title}`, error, {
        context: 'CardAjust_createCardToken',
        contractCode
      });
    }
  };

  const watchedFields = watch();
  useEffect(() => {
    const isEmpty: boolean =
      !watchedFields.number || !watchedFields.cvv || !watchedFields.expiry || !watchedFields.name;
    setAllIsEmpty(!isEmpty);
  }, [watchedFields, errors]);
  return (
    <>
      <Box sx={cardFormContainer}>
        {showCard && (
          <Box sx={{ ...headerContainer, paddingBottom: '0' }}>
            <CardComponent control={control} focused={focused as Focused} />
          </Box>
        )}
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box sx={cardFormInputsContainer}>
            <Controller
              name="number"
              control={control}
              render={({ field }) => (
                <>
                  <Input
                    label="Número do cartão"
                    onFocus={() => setfocused('number')}
                    placeholder="XXXX XXXX XXXX XXXX"
                    mask={'cardNumber'}
                    isDirty={dirtyFields.number}
                    error={errors?.number?.message ? true : false}
                    helperText={errors?.number?.message}
                    {...field}
                  />
                </>
              )}
            />
            <Controller
              name="cvv"
              control={control}
              render={({ field }) => (
                <>
                  <Input
                    onFocus={() => setfocused('cvc')}
                    label="CVV"
                    placeholder="XXX"
                    mask={'cvv'}
                    isDirty={dirtyFields.cvv}
                    error={errors?.cvv?.message ? true : false}
                    helperText={errors?.cvv?.message}
                    {...field}
                  />
                </>
              )}
            />
          </Box>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <>
                <Input
                  label="Nome no cartão"
                  onFocus={() => setfocused('number')}
                  placeholder="exatamente como está impresso"
                  isDirty={dirtyFields.name}
                  error={errors?.name?.message ? true : false}
                  helperText={errors?.name?.message}
                  {...field}
                />
              </>
            )}
          />
          <Box sx={inputValidtyFormContainer}>
            <Controller
              name="expiry"
              control={control}
              render={({ field }) => (
                <>
                  <Input
                    label="Expira"
                    onFocus={() => setfocused('number')}
                    placeholder="MM/AA"
                    mask={'expiredDate'}
                    // sx={{ width: '20rem' }}
                    error={errors?.expiry?.message ? true : false}
                    isDirty={dirtyFields.expiry}
                    helperText={errors?.expiry?.message}
                    {...field}
                  />
                </>
              )}
            />
          </Box>
          <Box sx={{ ...headerContainer, display: 'flex', paddingBottom: '0' }}>
            <LoadingButton
              variant="outlined"
              disabled={!allIsEmpty || loadingPublicKey || loading}
              loading={loading || loadingPublicKey}
              sx={{ marginX: 'auto' }}
              type="submit"
            >
              Alterar Cartão
            </LoadingButton>
          </Box>
        </form>
      </Box>
    </>
  );
}
